/**
 * AnimalRepository - Repositorio unificado para Animales
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 * Usa event sourcing: cada cambio crea un evento en animales_historico.
 */

import { v4 as uuidv4 } from "uuid";
import {
  IAnimalRepository,
  AnimalFilters,
  AnimalStatistics,
  AnimalPaginatedResult
} from "../interfaces/IAnimalRepository";
import { Animal, AnimalHistorico, Ubicacion } from "../../../types/interfaces";
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { emitLocalInsert, emitLocalUpdate, emitLocalDelete } from "../syncHelpers";
import { repositoryEvents } from "../RepositoryChangeEventEmitter";
import { normalizeCaravana } from "../../../utils/animales";
import { syncAnimalesLocal, matchesDateQuery } from "../../../utils/animales";
import { AnimalModel } from "../../../models/index";
import { createDate } from '../../../utils/dateHelper';
import { getRazaNombre } from '../../../utils/razasHelper';
import { buildNewCaravanaMap } from '../../../helpers/recaravaneoHelper';
import { matchesCaravanaSearch } from '../../../helpers/caravanaSearchHelper';
import { generateSessionCode } from '../../../helpers/sessionCode';
import { computeGdp, estimatePesoActual } from '../../../utils/gdp';
import { calculateFertility } from '../../../utils/fertility';
import { getDefaultGenderForCategory } from '../../../utils/categoria';
import { calcularPesoPromedioPorSexo, calcularConteoPorSexo, calcularGdpPorSexo } from '../../../utils/sexoStats';

/**
 * Tasa de fertilidad (%): delega en el helper puro compartido `calculateFertility`
 * (KAN-185/KAN-186). Mismo cálculo que el cell renderer de AnimalList.
 * Devuelve 0 si no hay datos.
 */
function computeFertilityRate(animal: Animal): number {
  return calculateFertility(animal).fertilityRate;
}

/**
 * Días transcurridos desde el último pesaje registrado (o fecha de evento).
 * Usado para ordenar la columna calculada "Días s/Pesaje".
 */
function daysSincePesaje(animal: Animal): number {
  const fechaStr = animal.fecha_ultimo_evento_peso ?? animal.fecha_de_evento;
  if (!fechaStr) return -1; // sin fecha → al fondo (asc) o arriba (desc) según signo
  const ms = Date.now() - new Date(fechaStr).getTime();
  if (!isFinite(ms) || ms < 0) return 0;
  return Math.floor(ms / 86400000);
}

/**
 * Repositorio unificado de Animales con event sourcing
 */
export class AnimalRepository extends BaseRepository<Animal, string> implements IAnimalRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'animales', null); // animales no se sincroniza directamente, solo animales_historico

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  /** PK de animales es `caravana` (no `id`). */
  protected get pkField(): string {
    return 'caravana';
  }

  /**
   * Override de getAll(): excluye los animales marcados is_deleted=true.
   *
   * A diferencia del BaseRepository (que filtra `is_deleted === false` cuando
   * supportsSoftDelete=true), acá usamos `is_deleted !== true` (falsy):
   * muchas filas de animales se crean localmente (add/update/recaravaneo) ANTES
   * de tener el campo `is_deleted` cargado (llega recién al pullear desde
   * Supabase), y quedarían con `undefined`. Un filtro estricto `=== false` las
   * excluiría y haría desaparecer animales vivos. Solo se considera borrado el
   * que tiene `is_deleted === true` (seteado por el trigger de animales_historico
   * y conservado localmente por el pull).
   *
   * Heredan este filtro: getAllForActiveEmpresa, getAllFiltered, getAllPaginated
   * y las estadísticas.
   */
  async getAll(): Promise<Animal[]> {
    const all = await this.db.getAll<Animal>('animales');
    return all.filter(a => !(a as any)?.is_deleted);
  }

  /**
   * Obtiene repositorios auxiliares (lazy loading para evitar dependencia circular)
   */
  private async getAuxiliaryRepos() {
    const { getRepositories } = await import('../index');
    const repos = getRepositories();
    return {
      ubicacion: repos.ubicacion
    };
  }

  /**
   * Catálogo id→nombre de ubicación (ya en minúsculas) para el searchTerm:
   * la búsqueda de Mi Ganado también matchea la ubicación del animal
   * ("corral 2" encuentra los animales de "Corral 2", KAN-201). Si el catálogo
   * no puede cargarse, la búsqueda sigue sin este match (no debe romper).
   */
  private async nombresUbicacionParaBusqueda(): Promise<Map<string, string> | undefined> {
    try {
      const { ubicacion } = await this.getAuxiliaryRepos();
      const ubicaciones = await ubicacion.getAll();
      const map = new Map<string, string>();
      for (const u of ubicaciones) map.set(u.id, (u.nombre ?? '').toLowerCase());
      return map;
    } catch {
      return undefined;
    }
  }

  /**
   * Lookup por caravana scopeado a la EMPRESA ACTIVA.
   *
   * La PK local de animales es compuesta (empresa_id, caravana): la misma
   * caravana puede existir en varias empresas y convivir en el caché local.
   * Este override resuelve la empresa activa del profile y hace el lookup por
   * la clave compuesta, así TODOS los callers que hacen get(caravana) quedan
   * automáticamente scopeados por empresa (incluido el merge de recaravaneo).
   */
  async get(caravana: string): Promise<Animal | undefined> {
    const empresaId = (await this.getCurrentProfile())?.empresa_activa_id;
    if (!empresaId) return undefined;
    return this.db.get<Animal>('animales', [empresaId, caravana]);
  }

  /**
   * Override de bulkDeleteSilent: animales usa PK compuesta (empresa_id,
   * caravana). Normaliza los ids que recibe:
   *  - string (caravana)  → resuelve empresa ACTIVA → [empresaActiva, caravana]
   *    (caso del realtime/catch-up, que opera sobre el canal de la empresa
   *     activa y solo tiene la caravana).
   *  - string[] (compuesta) → se usa tal cual (caso del pull de Supabase, que
   *    trae la empresa_id real de cada fila y puede abarcar varias empresas).
   */
  async bulkDeleteSilent(ids: (string | string[])[]): Promise<void> {
    if (ids.length === 0) return;
    const empresaId = (await this.getCurrentProfile())?.empresa_activa_id;
    const compositeKeys: string[][] = ids.map(id =>
      Array.isArray(id) ? id : [empresaId as string, id]
    );
    await this.db.bulkDelete('animales', compositeKeys);
    compositeKeys.forEach(([, caravana]) => {
      this.emitLocalSync('DELETE', caravana);
    });
  }

  /**
   * Espejo local OPTIMISTA del renombrado de caravana (spec
   * alta-masiva-sin-caravana §3.6): la acción `reasignar_historial` se ejecuta
   * server-side cuando llegue al server; esto refleja el resultado final en
   * el caché local `animales` para que la UI lo muestre ya:
   *  - Si el destino no existe localmente, se crea como copia del origen con
   *    la caravana nueva (si existe, no se toca: el merge real lo hace el
   *    recompute server-side y llega por pull).
   *  - La fila del origen se borra del caché (silent: `animales` no se
   *    sincroniza, se regenera server-side).
   */
  async espejoLocalRenombradoCaravana(origen: string, destino: string): Promise<void> {
    const empresaId = (await this.getCurrentProfile())?.empresa_activa_id;
    if (!empresaId) return;

    const origenRow = await this.db.get<Animal>('animales', [empresaId, origen]);
    if (origenRow) {
      const destinoRow = await this.db.get<Animal>('animales', [empresaId, destino]);
      if (!destinoRow) {
        const nuevaFila = { ...origenRow, caravana: destino };
        await this.db.put('animales', nuevaFila);
        this.emitLocalSync('INSERT', nuevaFila);
      }
      await this.db.delete('animales', [empresaId, origen]);
      this.emitLocalSync('DELETE', origen);
    }
  }

  /**
   * Override de deleteWhereEmpresaNotInSilent: animales usa PK compuesta, así
   * que las claves a borrar son [empresa_id, caravana] (no la caravana sola).
   * Los registros sin empresa_id (legacy) se borran por su caravana con la
   * empresa que tengan (si la tienen).
   */
  async deleteWhereEmpresaNotInSilent(allowedEmpresaIds: string[]): Promise<number> {
    const allowed = new Set(allowedEmpresaIds);
    const all = await this.db.getAll<Animal>('animales');
    const toDelete: string[][] = [];
    for (const rec of all) {
      const emp = (rec as any)?.empresa_id;
      const caravana = (rec as any)?.caravana;
      if (caravana === undefined || caravana === null) continue;
      const notAllowed = typeof emp !== 'string' || emp === '' || !allowed.has(emp);
      if (notAllowed && emp) {
        toDelete.push([emp, caravana]);
      }
    }
    if (toDelete.length === 0) return 0;
    await this.db.bulkDelete('animales', toDelete);
    toDelete.forEach(([, caravana]) => {
      this.emitLocalSync('DELETE', caravana);
    });
    return toDelete.length;
  }

  /**
   * Agrega un único evento de animal. Delega en `bulkAdd([data])` para que TODO
   * write de animales_historico salga como un único BULK_INSERT (1 evento =
   * 1 upsert online; offline el batcher agrupa los pendientes al reconectarse),
   * en vez de un INSERT individual por registro que multiplica llamadas y
   * broadcasts a Supabase.
   *
   * Mantiene la firma `Promise<string>` por compatibilidad (siempre devolvió
   * la cadena vacía). `data` se muta igual que antes: `id`,
   * `fecha_de_creacion` y `session_code` (si faltaba) quedan seteados en el
   * objeto del caller.
   */
  async add(data: AnimalHistorico): Promise<string> {
    await this.bulkAdd([data]);
    return "";
  }

  // @ts-ignore - AnimalRepository uses AnimalHistorico which is different from Partial<Animal>
  async update(data: any): Promise<void> {
    await this.add(data);
  }

  /**
   * Agrega múltiples eventos de animales en una sola operación bulk.
   *
   * A diferencia de llamar a add() en un loop (que emite N eventos INSERT a
   * animales_historico y por ende N llamadas a Supabase), este método:
   *   1. Procesa cada animal localmente (igual que add())
   *   2. Acumula todos los registros de animales_historico
   *   3. Emite un ÚNICO evento BULK_INSERT para que SyncManager haga una sola
   *      operación bulk (upsert del array completo) en Supabase.
   *
   * Devuelve `void` para conformar el contrato de `BaseRepository.bulkAdd`
   * (alias de `bulkInsert`, que es `Promise<void>`). Los IDs generados para
   * cada evento quedan disponibles en los registros del BULK_INSERT emitido.
   */
  override async bulkAdd(dataList: AnimalHistorico[]): Promise<void> {
    if (dataList.length === 0) return;

    const profile = await this.getCurrentProfile();
    const empresaId = profile?.empresa_activa_id;
    const userId = profile?.user_id;
    const now = new Date().toISOString();

    // Un session_code compartido para toda la operación bulk; se asigna a los
    // registros que no traigan uno propio (invariante: todo insert lleva session_code).
    const bulkSessionCode = generateSessionCode();

    const historicoRecordsForSync: Partial<AnimalHistorico>[] = [];

    for (const data of dataList) {
      data.id = uuidv4();
      data.fecha_de_creacion = now;
      data.ubicacion_id = data.ubicacion_id === '' ? undefined : data.ubicacion_id;
      if (data.session_code == null) {
        data.session_code = bulkSessionCode;
      }

      // Garantía de sexo: todo animal no-TERNERO lleva sexo por categoría.
      this.ensureSexo(data);

      const currentAnimal = await this.get(data.caravana);
      const updatedAnimal = syncAnimalesLocal(currentAnimal, data);

      // PK compuesta: la fila local necesita empresa_id para derivar la clave.
      if (updatedAnimal) {
        (updatedAnimal as any).empresa_id = empresaId ?? (updatedAnimal as any).empresa_id;
      }

      if (data.estado === "BORRADO" && currentAnimal) {
        await this.db.delete('animales', [empresaId as string, data.caravana]);
        emitLocalDelete('animales', data.caravana);
      }
      else if (updatedAnimal && currentAnimal) {
        const { id, ubicacion_nombre, ...animalForInsert } = (updatedAnimal as any);
        await this.db.put('animales', animalForInsert);
        emitLocalUpdate('animales', updatedAnimal as unknown as Record<string, unknown>);
      }
      else if (updatedAnimal) {
        const { id, ubicacion_nombre, ...animalForInsert } = (updatedAnimal as any);
        await this.db.insert('animales', animalForInsert);
        emitLocalInsert('animales', updatedAnimal as unknown as Record<string, unknown>);
      }

      // Construir el registro histórico para sync (misma lógica que add())
      const animalBaseForSync: Partial<AnimalHistorico> = {
        id: data.id,
        caravana: data.caravana,
        fecha_de_creacion: data.fecha_de_creacion,
        fecha_de_evento: data.fecha_de_evento,
        empresa_id: empresaId,
        user_id: userId
      };
      this.populateOptionalFields(animalBaseForSync, data);
      historicoRecordsForSync.push(animalBaseForSync);
    }

    // Emitir un ÚNICO evento BULK_INSERT con todos los registros históricos
    if (historicoRecordsForSync.length > 0) {
      repositoryEvents.emit(
        'animales_historico',
        'BULK_INSERT',
        historicoRecordsForSync as Record<string, unknown>[]
      );
    }
  }

  /**
   * Actualiza múltiples animales en una sola operación bulk, siguiendo el patrón
   * event-sourcing (igual que AnimalHistoricoService.bulkUpdate, pero integrado
   * al repositorio para que SyncManager maneje la sincronización: cola offline,
   * reintento, lock SW↔app y chunking).
   *
   * Para cada registro: soft-deletea el evento viejo (is_deleted:true por id en
   * animales_historico) e inserta un evento nuevo (nuevo id + session_code
   * compartido). Actualiza además el caché local `animales`.
   *
   * Emite DOS eventos a animales_historico:
   *   - BULK_UPDATE: soft-delete de los ids viejos (merge upsert por id con
   *     defaultToNull:false, sin updated_at — la tabla no tiene esa columna).
   *   - BULK_INSERT: insert de los nuevos eventos.
   *
   * `items` debe traer el `id` viejo (se usa para soft-delete) más los campos
   * mergeados; el nuevo `id`, `fecha_de_creacion` y `session_code` los genera
   * este método. Registros sin `id` se tratan como eventos nuevos.
   */
  // @ts-ignore - AnimalRepository usa AnimalHistorico (distinto de Partial<Animal>)
  override async bulkUpdate(items: AnimalHistorico[]): Promise<void> {
    if (items.length === 0) return;

    const profile = await this.getCurrentProfile();
    const empresaId = profile?.empresa_activa_id;
    const userId = profile?.user_id;
    // Base temporal compartida; cada evento nuevo lleva un fecha_de_creacion
    // estrictamente creciente (+1ms por índice) para preservar el orden entre
    // eventos del mismo lote (mismo patrón que el service original).
    const baseDate = new Date();

    // Un session_code compartido para toda la operación bulk; se asigna a los
    // registros que no traigan uno propio (invariante: todo insert lleva session_code).
    const bulkSessionCode = generateSessionCode();

    const historicoRecordsForSync: Partial<AnimalHistorico>[] = [];
    const oldIdsToSoftDelete: string[] = [];

    for (const [index, item] of items.entries()) {
      // El id viejo se soft-deletea; el nuevo evento lleva un id nuevo.
      // Clonar para que el caller conserve el registro viejo intacto.
      if (item.id) oldIdsToSoftDelete.push(item.id);

      const data: AnimalHistorico = { ...item };
      data.id = uuidv4();
      data.fecha_de_creacion = new Date(baseDate.getTime() + index).toISOString();
      data.ubicacion_id = data.ubicacion_id === '' ? undefined : data.ubicacion_id;
      if (data.session_code == null) {
        data.session_code = bulkSessionCode;
      }

      // Garantía de sexo: todo animal no-TERNERO lleva sexo por categoría.
      this.ensureSexo(data);

      const currentAnimal = await this.get(data.caravana);
      const updatedAnimal = syncAnimalesLocal(currentAnimal, data);

      // PK compuesta: la fila local necesita empresa_id para derivar la clave.
      if (updatedAnimal) {
        (updatedAnimal as any).empresa_id = empresaId ?? (updatedAnimal as any).empresa_id;
      }

      if (data.estado === "BORRADO" && currentAnimal) {
        await this.db.delete('animales', [empresaId as string, data.caravana]);
        emitLocalDelete('animales', data.caravana);
      }
      else if (updatedAnimal && currentAnimal) {
        const { id, ubicacion_nombre, ...animalForInsert } = (updatedAnimal as any);
        await this.db.put('animales', animalForInsert);
        emitLocalUpdate('animales', updatedAnimal as unknown as Record<string, unknown>);
      }
      else if (updatedAnimal) {
        const { id, ubicacion_nombre, ...animalForInsert } = (updatedAnimal as any);
        await this.db.insert('animales', animalForInsert);
        emitLocalInsert('animales', updatedAnimal as unknown as Record<string, unknown>);
      }

      // Construir el registro histórico para sync (misma lógica que add()/bulkAdd())
      const animalBaseForSync: Partial<AnimalHistorico> = {
        id: data.id,
        caravana: data.caravana,
        fecha_de_creacion: data.fecha_de_creacion,
        fecha_de_evento: data.fecha_de_evento,
        empresa_id: empresaId,
        user_id: userId
      };
      this.populateOptionalFields(animalBaseForSync, data);
      historicoRecordsForSync.push(animalBaseForSync);
    }

    // Espejo local del soft-delete (best-effort): marcar is_deleted=true en el
    // caché de animales_historico para que las lecturas locales (que filtran
    // is_deleted) dejen de mostrar los eventos corregidos. Merge parcial
    // silencioso (no emite sync); no-op para ids no cacheados localmente.
    if (oldIdsToSoftDelete.length > 0) {
      try {
        await this.db.bulkUpdate(
          'animales_historico',
          oldIdsToSoftDelete.map(id => ({ id, is_deleted: true }))
        );
      } catch (e) {
        console.warn('[AnimalRepository.bulkUpdate] Espejo local del soft-delete falló (no crítico):', e);
      }
    }

    // Soft-delete de los eventos viejos (merge upsert por id con is_deleted:true).
    // defaultToNull:false en saveRemote hace que solo se actualice is_deleted.
    if (oldIdsToSoftDelete.length > 0) {
      repositoryEvents.emit(
        'animales_historico',
        'BULK_UPDATE',
        oldIdsToSoftDelete.map(id => ({ id, is_deleted: true })) as Record<string, unknown>[]
      );
    }

    // Insert de los nuevos eventos
    if (historicoRecordsForSync.length > 0) {
      repositoryEvents.emit(
        'animales_historico',
        'BULK_INSERT',
        historicoRecordsForSync as Record<string, unknown>[]
      );
    }
  }

  /**
   * Garantía de persistencia: si el animal tiene categoría no-TERNERO y no trae
   * sexo, le asigna el sexo implícito de la categoría (p.ej. VACA→H, NOVILLO→M).
   * Solo los terneros pueden quedar sin sexo (ternero/ternera lo elige el usuario).
   * MUTA `data` para que tanto el cache local (animales) como el histórico
   * (animales_historico → Supabase) lleven el sexo correcto.
   */
  private ensureSexo(data: AnimalHistorico): void {
    if (data && data.categoria && !data.sexo) {
      const sexo = getDefaultGenderForCategory(data.categoria);
      if (sexo) data.sexo = sexo;
    }
  }

  /**
   * Popula los campos opcionales de source a target si tienen valor definido.
   * (Misma lógica que add(), extraída como helper reutilizable).
   */
  private populateOptionalFields(target: Partial<AnimalHistorico>, source: AnimalHistorico): void {
    if (source.sexo !== undefined && source.sexo !== '') target.sexo = source.sexo;
    if (source.peso !== undefined) target.peso = source.peso;
    if (source.ubicacion_id !== undefined) target.ubicacion_id = source.ubicacion_id;
    if (source.raza !== undefined && source.raza !== '') target.raza = source.raza;
    if (source.observaciones !== undefined) target.observaciones = source.observaciones;
    if (source.categoria !== undefined) target.categoria = source.categoria;
    if (source.tiene_ternero !== undefined) target.tiene_ternero = source.tiene_ternero;
    if (source.edad !== undefined) target.edad = source.edad;
    if (source.prenies !== undefined) target.prenies = source.prenies;
    if (source.estado !== undefined) target.estado = source.estado;
    if (source.es_comprado !== undefined) target.es_comprado = source.es_comprado;
    if (source.caravanas_viejas !== undefined) target.caravanas_viejas = source.caravanas_viejas;
    if (source.fecha_nacimiento !== undefined) target.fecha_nacimiento = source.fecha_nacimiento;
    if (source.info_compra !== undefined) target.info_compra = source.info_compra;
    if (source.info_venta !== undefined) target.info_venta = source.info_venta;
    if (source.operacion_venta_id !== undefined) target.operacion_venta_id = source.operacion_venta_id;
    if (source.operacion_compra_id !== undefined) target.operacion_compra_id = source.operacion_compra_id;
    if (source.session_code !== undefined) target.session_code = source.session_code;
    if (source.renspa !== undefined) target.renspa = source.renspa;
    if (source.madre !== undefined) target.madre = source.madre;
    if (source.padre !== undefined) target.padre = source.padre;
  }

  /**
   * Borra un animal (event sourcing: INSERT con estado BORRADO). El borrado
   * individual desde fuera del form de carga lleva SESIÓN PROPIA (regla
   * KAN-208): no se cuelga de la sesión activa. El tachito del form/editor
   * de sesión usa eliminarDeSesion; el masivo usa bulkDelete (sesión
   * compartida para todo el lote).
   */
  /**
   * Borra un animal (evento BORRADO del event sourcing + fila local). Si NO
   * encuentra el animal, lanza error (antes era un no-op silencioso y los
   * llamadores —ej. la limpieza de práctica del tutorial— lo daban por
   * borrado sin haberlo tocado). Tolerante a normalización de caravana
   * (espacios/case) vía búsqueda de respaldo.
   */
  async delete(caravana: string): Promise<void> {
    let currentAnimal = await this.get(caravana);
    if (!currentAnimal) {
      // Respaldo: la caravana con la que nos llaman puede diferir en
      // espacios/case de la guardada (PK exacta). Buscar por normalizada.
      try {
        const objetivo = normalizeCaravana(caravana);
        const todos = await this.getAllForActiveEmpresa();
        currentAnimal = todos.find(a => normalizeCaravana(a.caravana) === objetivo);
      } catch {
        /* se reporta como no encontrado abajo */
      }
    }
    const profile = await this.getCurrentProfile();

    if (!currentAnimal) {
      throw new Error(`[AnimalRepository.delete] animal no encontrado: ${caravana}`);
    }

    const cleanHistoricoData: Record<string, unknown> = {
      id: uuidv4(),
      caravana: currentAnimal.caravana,
      fecha_de_creacion: new Date().toISOString(),
      fecha_de_evento: createDate(),
      estado: "BORRADO",
      session_code: generateSessionCode()
    };

    // Solo incluir empresa_id y user_id si están disponibles
    if (profile?.empresa_activa_id) {
      cleanHistoricoData.empresa_id = profile.empresa_activa_id;
    }
    if (profile?.user_id) {
      cleanHistoricoData.user_id = profile.user_id;
    }

    await this.db.delete('animales', [profile?.empresa_activa_id as string, currentAnimal.caravana]);

    // Emit event to sync with animales_historico in Supabase
    repositoryEvents.emit('animales_historico', 'INSERT', cleanHistoricoData);

    // Emitir evento local para que los componentes se actualicen
    emitLocalDelete('animales', currentAnimal.caravana);
  }

  async getByUbicacion(ubicacionId: string): Promise<Animal[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(a => a.ubicacion_id === ubicacionId);
  }

  async getByCaravanas(caravanas: string[]): Promise<Animal[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(a => caravanas.includes(a.caravana));
  }

  async getByEstado(estado: string): Promise<Animal[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(a => a.estado === estado);
  }

  async getVendidosByOperacionVentaIds(operacionIds: string[]): Promise<Animal[]> {
    if (operacionIds.length === 0) return [];

    const operacionIdsSet = new Set(operacionIds);
    const vendidos = await this.getByEstado('VENDIDO');

    return vendidos.filter(animal =>
      animal.operacion_venta_id !== undefined &&
      animal.operacion_venta_id !== null &&
      operacionIdsSet.has(animal.operacion_venta_id)
    );
  }

  /**
   * Helper privado para aplicar filtros de ubicación y unidad
   */
  private async applyLocationFilters(
    animales: Animal[],
    filters: AnimalFilters
  ): Promise<Animal[]> {
    let results = animales;

    // Filtro por ubicación (filtrado directo por ubicacion_id)
    if (filters.ubicacion && filters.ubicacion.length > 0) {
      results = results.filter(a =>
        a.ubicacion_id !== null &&
        a.ubicacion_id !== undefined &&
        filters.ubicacion!.includes(a.ubicacion_id)
      );
    }

    // Filtro por unidad (filtrado a través de la relación ubicacion)
    if (filters.unidad && filters.unidad.length > 0) {
      const { ubicacion } = await this.getAuxiliaryRepos();
      const ubicaciones = await ubicacion.getAll();
      const ubicacionIdsForUnidad = ubicaciones
        .filter((u: Ubicacion) => filters.unidad!.includes(u.unidad_id?.toString() || ''))
        .map((u: Ubicacion) => u.id);

      results = results.filter(a =>
        a.ubicacion_id !== null &&
        a.ubicacion_id !== undefined &&
        ubicacionIdsForUnidad.includes(a.ubicacion_id)
      );
    }

    return results;
  }

  /**
   * Aplica filtros complejos a la lista de animales
   *
   * `nombreUbicacionById` (id→nombre en minúsculas) habilita que el searchTerm
   * también matchee la ubicación del animal; sin él, la búsqueda no considera
   * ubicaciones (comportamiento previo al fix KAN-201).
   */
  private applyFilters(
    animales: Animal[],
    filters: AnimalFilters,
    nombreUbicacionById?: Map<string, string>
  ): Animal[] {
    const { Enums } = require("../../../types/enums");

    let results = animales;

    if (filters.estado && filters.estado.length > 0) {
      if (filters.estado.includes('invalid')) {
        results = results.filter(a => !Enums.ESTADO.values.includes(a.estado));
      } else {
        results = results.filter(a => filters.estado!.includes(a.estado!));
      }
    }

    if (filters.raza && filters.raza.length > 0) {
      if (filters.raza.includes('invalid')) {
        results = results.filter(a => !a.raza || a.raza === '');
      } else {
        results = results.filter(a => filters.raza!.includes(a.raza || ''));
      }
    }

    if (filters.categoria && filters.categoria.length > 0) {
      if (filters.categoria.includes('invalid')) {
        results = results.filter(a => !Enums.CATEGORIA.values.includes(a.categoria));
      } else {
        results = results.filter(a => filters.categoria!.includes(a.categoria!));
      }
    }

    if (filters.tipoEdad && filters.tipoEdad.length > 0) {
      if (filters.tipoEdad.includes('invalid')) {
        results = results.filter(a => !Enums.TIPO_EDAD.values.includes(a.edad));
      } else {
        results = results.filter(a => filters.tipoEdad!.includes(a.edad!));
      }
    }

    if (filters.esComprado && filters.esComprado.length > 0) {
      if (filters.esComprado.includes('invalid')) {
        results = results.filter(a => a.es_comprado === null || a.es_comprado === undefined);
      } else {
        const booleanFilters = filters.esComprado.map(f => f === 'true');
        results = results.filter(a =>
          a.es_comprado !== null &&
          a.es_comprado !== undefined &&
          booleanFilters.includes(a.es_comprado)
        );
      }
    }

    if (filters.prenies && filters.prenies.length > 0) {
      if (filters.prenies.includes('invalid')) {
        results = results.filter(a => !Enums.TIPO_PRENIEZ.values.includes(a.prenies));
      } else {
        results = results.filter(a => filters.prenies!.includes(a.prenies || ''));
      }
    }

    if (filters.renspa && filters.renspa.length > 0) {
      if (filters.renspa.includes('invalid')) {
        results = results.filter(a => !a.renspa);
      } else {
        results = results.filter(a => filters.renspa!.includes(a.renspa || ''));
      }
    }

    // Parentesco: substring sobre la caravana del progenitor RESUELTA a la
    // caravana actual de la cadena de recaravaneo (regla CLAUDE.md §3).
    // El mapa se construye UNA vez sobre el dataset completo (antes de filtrar).
    if (filters.madre || filters.padre || filters.searchTerm) {
      const newCaravanaByOld = buildNewCaravanaMap(results);
      const resolveDisplay = (ref?: string): string => {
        if (!ref) return '';
        return newCaravanaByOld.get(ref.trim().toLowerCase()) ?? ref;
      };
      if (filters.madre) {
        const q = filters.madre.toLowerCase();
        results = results.filter(a => resolveDisplay(a.madre).toLowerCase().includes(q));
      }
      if (filters.padre) {
        const q = filters.padre.toLowerCase();
        results = results.filter(a => resolveDisplay(a.padre).toLowerCase().includes(q));
      }

      if (filters.searchTerm) {
        const q = filters.searchTerm.toLowerCase();

        results = results.filter(a => {
          const animalModel = new AnimalModel(a);
          const formattedLastUpdate = animalModel.getFormattedLastUpdateDate();

          // KAN-206: el buscador también matchea las caravanas viejas del
          // animal. La caravana vieja vive como fila en el RECARAVANEADO
          // (excluido por el filtro default de estado) y como dato en su
          // reemplazo: sin este match, buscar una vieja parcial dejaba la
          // lista vacía aunque hubiera decenas de coincidencias.
          const matchesCaravana = matchesCaravanaSearch(a.caravana!, q) ||
            (a.caravanas_viejas ?? []).some((vieja) => matchesCaravanaSearch(vieja, q));
          const matchesRaza = (a.raza?.toLowerCase().includes(q) ?? false) ||
                              (a.raza && getRazaNombre(a.raza).toLowerCase().includes(q));
          const matchesCategoria = (a.categoria?.toLowerCase().includes(q) ?? false);
          const matchesEdad = (a.edad?.toString().toLowerCase().includes(q) ?? false);
          const matchesCreationDate = (a.fecha_de_creacion?.toLowerCase().includes(q) ?? false);
          const matchesLastUpdate = matchesDateQuery(q, formattedLastUpdate);
          const matchesRenspa = (a.renspa?.toLowerCase().includes(q) ?? false);
          const matchesMadre = resolveDisplay(a.madre).toLowerCase().includes(q);
          const matchesPadre = resolveDisplay(a.padre).toLowerCase().includes(q);
          // KAN-201: "corral 2"/"Corral 2"/"CORRAL 2" devuelven lo mismo — el
          // nombre se normalizó a minúsculas al armar el catálogo.
          const matchesUbicacion = !!(a.ubicacion_id &&
            nombreUbicacionById?.get(a.ubicacion_id)?.includes(q));

          // Also search by fecha_de_evento (event date)
          let formattedEventDate = '';
          if (a.fecha_de_evento) {
            const dateStr = a.fecha_de_evento.includes('T')
              ? a.fecha_de_evento
              : a.fecha_de_evento + 'T00:00:00';
            const date = new Date(dateStr);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            formattedEventDate = `${day}/${month}/${year}`;
          }
          const matchesEventDate = matchesDateQuery(q, formattedEventDate);

          return matchesCaravana || matchesRaza || matchesCategoria || matchesEdad ||
            matchesCreationDate || matchesLastUpdate || matchesEventDate || matchesRenspa ||
            matchesMadre || matchesPadre || matchesUbicacion;
        });
      }
    }

    return results;
  }

  /**
   * Ordena animales según campo (siempre ascendente, usar reverse() para descendente)
   *
   * Soporta columnas calculadas (`calc_*`): se computa el valor derivado de cada
   * animal en memoria y se ordena por ese valor. El orden es correcto a nivel del
   * dataset completo (no por página), porque getAllFiltered opera sobre todos los
   * animales filtrados antes de paginar.
   */
  private sortAnimales(animales: Animal[], sortBy?: string): Animal[] {
    if (!sortBy) return animales;

    // ── Columnas calculadas: ordenar por valor derivado ──
    if (sortBy.startsWith('calc_')) {
      const getCalcValue = (a: Animal): number => {
        switch (sortBy) {
          case 'calc_gdp':
            return computeGdp(a.pesajes, a.peso_inicial, a.fecha_de_evento_peso_inicial)?.gdp ?? 0;
          case 'calc_pesajes':
            return a.pesajes?.length ?? 0;
          case 'calc_fertilidad':
            return computeFertilityRate(a);
          case 'calc_peso_estimado':
            return estimatePesoActual(a);
          case 'calc_ganancia_total':
            return (a.peso ?? 0) - (a.peso_inicial ?? 0);
          case 'calc_dias_pesaje':
            return daysSincePesaje(a);
          default:
            return 0;
        }
      };
      return [...animales].sort((a, b) => {
        const diff = getCalcValue(a) - getCalcValue(b);
        return diff;
      });
    }

    // Comparator primario por la key pedida (una sola key, sin desempate).
    const compararPrimario = (a: Animal, b: Animal): number => {
      let aValue = a[sortBy as keyof Animal];
      let bValue = b[sortBy as keyof Animal];

      if (sortBy === 'lastUpdate') {
        const aModel = new AnimalModel(a);
        const bModel = new AnimalModel(b);
        aValue = aModel.getLastUpdateDate() || '';
        bValue = bModel.getLastUpdateDate() || '';
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return aDate.getTime() - bDate.getTime();
        }
        return aValue.localeCompare(bValue, 'es', { sensitivity: 'base' });
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return aValue.getTime() - bValue.getTime();
      }

      return String(aValue).localeCompare(String(bValue), 'es', { sensitivity: 'base' });
    };

    // Segundo orden para fecha_de_evento (default de Mi Ganado): a igual
    // fecha, gana el animal con última actualización más reciente — y la
    // caravana como desempate final para un orden determinístico.
    // OJO: la dirección la aplica getAllFiltered con reverse(), así que el
    // desempate queda coherente con la dirección del orden primario.
    const desempateFechaEvento = (a: Animal, b: Animal): number => {
      if (sortBy !== 'fecha_de_evento') return 0;
      const aUpd = new Date(new AnimalModel(a).getLastUpdateDate() || '').getTime();
      const bUpd = new Date(new AnimalModel(b).getLastUpdateDate() || '').getTime();
      if (!isNaN(aUpd) && !isNaN(bUpd) && aUpd !== bUpd) return aUpd - bUpd;
      return String(a.caravana).localeCompare(String(b.caravana), 'es');
    };

    return [...animales].sort(
      (a, b) => compararPrimario(a, b) || desempateFechaEvento(a, b),
    );
  }

  /**
   * Obtiene TODOS los animales que coinciden con los filtros (sin paginar).
   * Aplica los mismos filtros y ordenamiento que getAllPaginated, pero sin el slice.
   * Usado por la selección múltiple "seleccionar todo" para abarcar todas las páginas.
   */
  async getAllFiltered(
    filters?: AnimalFilters,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc'
  ): Promise<Animal[]> {
    // Filtrar por empresa activa para no mezclar datos de otras empresas.
    let animales = await this.getAllForActiveEmpresa();

    // Aplicar filtros
    if (filters) {
      const nombreUbicacionById = filters.searchTerm
        ? await this.nombresUbicacionParaBusqueda()
        : undefined;
      animales = this.applyFilters(animales, filters, nombreUbicacionById);
      animales = await this.applyLocationFilters(animales, filters);
    }

    // Aplicar ordenamiento
    if (sortBy) {
      animales = this.sortAnimales(animales, sortBy);
      if (sortDirection === 'desc') {
        animales.reverse();
      }
    }

    return animales;
  }

  async getAllPaginated(
    offset: number = 0,
    limit: number = 50,
    filters?: AnimalFilters,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc'
  ): Promise<AnimalPaginatedResult> {
    const animales = await this.getAllFiltered(filters, sortBy, sortDirection);

    const total = animales.length;
    const items = animales.slice(offset, offset + limit);

    return { animales: items, total };
  }

  async getTotalStatistics(): Promise<AnimalStatistics> {
    const all = await this.getAllForActiveEmpresa();

    const activeCount = all.filter(a => a.estado === "ACTIVO").length;
    const soldCount = all.filter(a => a.estado === "VENDIDO").length;

    let totalWeight = 0;
    let validWeightCount = 0;

    all.forEach((animal) => {
      if (animal.estado === 'RECARAVANEADO') return;
      if (animal.peso && animal.peso > 0) {
        totalWeight += animal.peso;
        validWeightCount++;
      }
    });

    const averageWeight = validWeightCount > 0 ? totalWeight / validWeightCount : 0;

    // Desglose por sexo (mismo scope que el promedio general: excluye RECARAVANEADO).
    const noRecaravaneado = all.filter(a => a.estado !== 'RECARAVANEADO');
    const { macho: pesoPromedioMacho, hembra: pesoPromedioHembra, sinSexo: pesoPromedioSinSexo } = calcularPesoPromedioPorSexo(noRecaravaneado);
    const { macho: activosMacho, hembra: activosHembra, sinSexo: activosSinSexo } = calcularConteoPorSexo(all.filter(a => a.estado === "ACTIVO"));
    const { general: gdpPromedio, macho: gdpMacho, hembra: gdpHembra, sinSexo: gdpSinSexo } = calcularGdpPorSexo(noRecaravaneado);

    return { activeCount, soldCount, totalWeight, averageWeight, pesoPromedioMacho, pesoPromedioHembra, pesoPromedioSinSexo, activosMacho, activosHembra, activosSinSexo, gdpPromedio, gdpMacho, gdpHembra, gdpSinSexo };
  }

  async getFilteredStatistics(filters?: AnimalFilters): Promise<AnimalStatistics> {
    let animales = await this.getAllForActiveEmpresa();

    const estadoExplicitlyIncludesRecaravaneado =
      filters?.estado && filters.estado.length > 0 &&
      filters.estado.some((e) => e && e.toUpperCase() === 'RECARAVANEADO');
    if (!estadoExplicitlyIncludesRecaravaneado) {
      animales = animales.filter((a) => a.estado !== 'RECARAVANEADO');
    }

    // Apply same filters as getAllPaginated
    if (filters) {
      const nombreUbicacionById = filters.searchTerm
        ? await this.nombresUbicacionParaBusqueda()
        : undefined;
      animales = this.applyFilters(animales, filters, nombreUbicacionById);
      animales = await this.applyLocationFilters(animales, filters);
    }

    let activeCount = 0;
    let soldCount = 0;
    let totalWeight = 0;
    let validWeightCount = 0;

    animales.forEach((animal) => {
      if (animal.estado === "ACTIVO") activeCount++;
      if (animal.estado === "VENDIDO") soldCount++;
      if (animal.peso && animal.peso > 0) {
        totalWeight += animal.peso;
        validWeightCount++;
      }
    });

    const averageWeight = validWeightCount > 0 ? totalWeight / validWeightCount : 0;

    // Desglose por sexo (mismo scope: animales filtrados).
    const { macho: pesoPromedioMacho, hembra: pesoPromedioHembra, sinSexo: pesoPromedioSinSexo } = calcularPesoPromedioPorSexo(animales);
    const { macho: activosMacho, hembra: activosHembra, sinSexo: activosSinSexo } = calcularConteoPorSexo(animales.filter(a => a.estado === "ACTIVO"));
    const { general: gdpPromedio, macho: gdpMacho, hembra: gdpHembra, sinSexo: gdpSinSexo } = calcularGdpPorSexo(animales);

    return { activeCount, soldCount, totalWeight, averageWeight, pesoPromedioMacho, pesoPromedioHembra, pesoPromedioSinSexo, activosMacho, activosHembra, activosSinSexo, gdpPromedio, gdpMacho, gdpHembra, gdpSinSexo };
  }

  async bulkDelete(caravanas: string[]): Promise<void> {
    if (caravanas.length === 0) return;

    const profile = await this.getCurrentProfile();
    const now = new Date().toISOString();
    const empresaId = profile?.empresa_activa_id;

    // Eliminar localmente con clave compuesta (empresa_id, caravana)
    const compositeKeys = caravanas.map(caravana => [empresaId as string, caravana]);
    await this.db.bulkDelete('animales', compositeKeys);
    caravanas.forEach(caravana => {
      emitLocalDelete('animales', caravana);
    });

    // Crear registros históricos BORRADO (event sourcing, consistente con delete())
    // y emitir un ÚNICO evento BULK_INSERT para una sola operación bulk en Supabase.
    // Todos los eventos del lote comparten el mismo session_code (invariante:
    // todo insert a animales_historico lleva session_code).
    const bulkSessionCode = generateSessionCode();
    const historicoRecords: Record<string, unknown>[] = caravanas.map(caravana => {
      const record: Record<string, unknown> = {
        id: uuidv4(),
        caravana,
        fecha_de_creacion: now,
        fecha_de_evento: createDate(),
        estado: "BORRADO",
        session_code: bulkSessionCode
      };
      if (profile?.empresa_activa_id) record.empresa_id = profile.empresa_activa_id;
      if (profile?.user_id) record.user_id = profile.user_id;
      return record;
    });

    repositoryEvents.emit('animales_historico', 'BULK_INSERT', historicoRecords);
  }

  async getLastByDate(dateColumn: string = 'updated_at'): Promise<Animal | undefined> {
    const all = await this.getAll();
    if (all.length === 0) return undefined;

    return all.reduce((latest, current) => {
      const latestDate = (latest as any)[dateColumn] || '';
      const currentDate = (current as any)[dateColumn] || '';
      return currentDate > latestDate ? current : latest;
    });
  }

  // getLastByDateForEmpresa se hereda de BaseRepository (usa db.getAll que
  // incluye soft-deletes, necesario para que el incremental traiga deletes).
}
